import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Info, X, Users, Trophy, Shuffle, Save, RotateCcw, Zap, Plus, Minus, Home, Share2, Copy, Check, Loader2, ExternalLink, MessageCircle, Pencil, UserPlus, UserMinus, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Animated Jass Cards Background Component
const JassCardsBackground = () => {
  const cards = [
    // Schellen (Bells)
    { suit: '🔔', value: 'A', color: '#D4AF37' },
    { suit: '🔔', value: 'K', color: '#D4AF37' },
    { suit: '🔔', value: 'O', color: '#D4AF37' },
    { suit: '🔔', value: 'U', color: '#D4AF37' },
    // Schilten (Shields)
    { suit: '🛡️', value: 'A', color: '#228B22' },
    { suit: '🛡️', value: 'K', color: '#228B22' },
    { suit: '🛡️', value: 'O', color: '#228B22' },
    { suit: '🛡️', value: 'U', color: '#228B22' },
    // Eicheln (Acorns)
    { suit: '🌰', value: 'A', color: '#8B4513' },
    { suit: '🌰', value: 'K', color: '#8B4513' },
    { suit: '🌰', value: 'O', color: '#8B4513' },
    { suit: '🌰', value: 'U', color: '#8B4513' },
    // Rosen (Roses)
    { suit: '🌹', value: 'A', color: '#DC143C' },
    { suit: '🌹', value: 'K', color: '#DC143C' },
    { suit: '🌹', value: 'O', color: '#DC143C' },
    { suit: '🌹', value: 'U', color: '#DC143C' },
  ];

  // Generate random positions for floating cards
  const floatingCards = Array.from({ length: 20 }, (_, i) => ({
    id: i,
    card: cards[i % cards.length],
    left: Math.random() * 100,
    delay: Math.random() * 20,
    duration: 15 + Math.random() * 20,
    size: 40 + Math.random() * 30,
    rotation: Math.random() * 360,
    rotationSpeed: (Math.random() - 0.5) * 2,
  }));

  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none z-0">
      {/* Gradient Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#264F77] via-[#1e3d5c] to-[#162d42]" />
      
      {/* Geometric Elements - Fixed Position */}
      <div className="absolute inset-0 overflow-hidden">
        {/* Large circle top left */}
        <div 
          className="absolute -top-20 -left-20 w-96 h-96 border-2 border-white/10 rounded-full"
          style={{ position: 'fixed', top: '-5rem', left: '-5rem' }}
        />
        {/* Medium circle top right */}
        <div 
          className="absolute top-40 -right-10 w-64 h-64 border-2 border-white/10 rounded-full"
          style={{ position: 'fixed', top: '10rem', right: '-2.5rem' }}
        />
        {/* Diagonal lines */}
        <svg className="absolute inset-0 w-full h-full" style={{ position: 'fixed' }}>
          <line x1="0" y1="100%" x2="30%" y2="60%" stroke="rgba(255,255,255,0.05)" strokeWidth="1" />
          <line x1="70%" y1="0" x2="100%" y2="40%" stroke="rgba(255,255,255,0.05)" strokeWidth="1" />
          <line x1="50%" y1="100%" x2="80%" y2="50%" stroke="rgba(255,255,255,0.03)" strokeWidth="1" />
        </svg>
      </div>

      {/* Floating Jass Cards */}
      {floatingCards.map((item) => (
        <div
          key={item.id}
          className="absolute animate-float-card"
          style={{
            left: `${item.left}%`,
            animationDelay: `${item.delay}s`,
            animationDuration: `${item.duration}s`,
          }}
        >
          <div
            className="bg-white/90 rounded-lg shadow-lg flex flex-col items-center justify-center backdrop-blur-sm"
            style={{
              width: `${item.size}px`,
              height: `${item.size * 1.4}px`,
              transform: `rotate(${item.rotation}deg)`,
              animation: `spin-slow ${20 + item.rotationSpeed * 10}s linear infinite`,
            }}
          >
            <span className="text-lg">{item.card.suit}</span>
            <span 
              className="font-bold text-sm"
              style={{ color: item.card.color }}
            >
              {item.card.value}
            </span>
          </div>
        </div>
      ))}

      {/* Additional static decorative cards */}
      <div className="absolute top-20 left-10 opacity-20 transform rotate-12">
        <div className="bg-white rounded-lg w-16 h-24 flex flex-col items-center justify-center shadow-xl">
          <span className="text-2xl">🔔</span>
          <span className="font-bold text-yellow-600">K</span>
        </div>
      </div>
      <div className="absolute top-40 right-20 opacity-15 transform -rotate-12">
        <div className="bg-white rounded-lg w-14 h-20 flex flex-col items-center justify-center shadow-xl">
          <span className="text-xl">🌹</span>
          <span className="font-bold text-red-600">A</span>
        </div>
      </div>
      <div className="absolute bottom-32 left-1/4 opacity-10 transform rotate-45">
        <div className="bg-white rounded-lg w-12 h-18 flex flex-col items-center justify-center shadow-xl">
          <span className="text-lg">🛡️</span>
          <span className="font-bold text-green-600">U</span>
        </div>
      </div>
      <div className="absolute bottom-20 right-1/3 opacity-15 transform -rotate-6">
        <div className="bg-white rounded-lg w-16 h-24 flex flex-col items-center justify-center shadow-xl">
          <span className="text-2xl">🌰</span>
          <span className="font-bold text-amber-700">O</span>
        </div>
      </div>

      {/* CSS for animations */}
      <style>{`
        @keyframes float-up {
          0% {
            transform: translateY(100vh) rotate(0deg);
            opacity: 0;
          }
          10% {
            opacity: 0.3;
          }
          90% {
            opacity: 0.3;
          }
          100% {
            transform: translateY(-100vh) rotate(360deg);
            opacity: 0;
          }
        }
        @keyframes spin-slow {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        .animate-float-card {
          animation: float-up linear infinite;
        }
      `}</style>
    </div>
  );
};

// Info Modal Component
const InfoModal = ({ isOpen, onClose }) => {
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 transition-opacity duration-200" />
      
      {/* Modal */}
      <div 
        className="relative bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto transform transition-all duration-200 animate-in fade-in zoom-in-95"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors z-10"
          aria-label="Schliessen"
        >
          <X size={20} className="text-gray-600" />
        </button>

        {/* Content */}
        <div className="p-6 md:p-8">
          {/* Helen Image */}
          <div className="flex justify-center mb-6">
            <img 
              src="https://customer-assets.emergentagent.com/job_realestate-ai-blog/artifacts/mlrf4r70_Helen.webp"
              alt="Helen"
              className="w-48 h-48 md:w-56 md:h-56 object-cover rounded-full shadow-lg"
            />
          </div>

          {/* Intro Text */}
          <p className="text-center text-gray-600 italic mb-8 text-lg">
            Falls ich einmal die Spielregeln ändern will, hier die korrekte Anleitung:
          </p>

          {/* Game Rules */}
          <div className="space-y-6">
            {/* Goal */}
            <div className="bg-[#264F77]/5 rounded-xl p-5">
              <h3 className="font-semibold text-[#264F77] text-lg mb-2">Spielziel</h3>
              <p className="text-gray-700 leading-relaxed">
                Ziel ist es, alle Karten loszuwerden. Der Spieler, der zuerst keine Karten mehr hat, 
                ruft <strong>„Tschau"</strong>, der letzte Spieler ist der <strong>„Sepp"</strong> und kassiert die Punkte.
              </p>
            </div>

            {/* Rules List */}
            <div className="space-y-4">
              <h3 className="font-semibold text-[#264F77] text-lg">Spielregeln</h3>
              
              <ul className="space-y-4 text-gray-700">
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-[#264F77] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span>Alle Karten werden ausgeteilt (Rest egal, je nach Spielerzahl)</span>
                </li>
                
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-[#264F77] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <div>
                    <strong>Spielablauf:</strong> Eine Karte wird ausgespielt. Die nächsten Spieler müssen 
                    die gleiche Zahl oder Farbe spielen. Kann oder will ein Spieler nicht, nimmt er 
                    Karten auf (meist 1 oder mehr, je nach Regel).
                  </div>
                </li>
                
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-[#264F77] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <div>
                    <strong>Letzte Karte:</strong> „Tschau" sagen. Vergisst man es → Strafe. 
                    Der letzte Spieler ist Sepp.
                  </div>
                </li>
                
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-[#264F77] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span>Punkte gehen gegen den Sepp</span>
                </li>
                
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-[#264F77] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span>Eine <strong>Doppelrunde</strong> muss vor dem Ablegen der ersten Karte durch den Spieler klar angesagt werden</span>
                </li>
                
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-[#264F77] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span>Endet eine Runde mit einer <strong>Sieben</strong>, kann der nächste Spieler eine weitere Sieben darauf legen, sofern er eine hat; dies kann sich beliebig fortsetzen</span>
                </li>
                
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-[#264F77] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span><strong>Under-Sepp</strong> bedeutet, dass die Spielrunde doppelt zählt</span>
                </li>
                
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-[#264F77] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span>Das Spiel verläuft <strong>gegen den Uhrzeigersinn</strong></span>
                </li>
                
                <li className="flex items-start">
                  <span className="w-2 h-2 bg-[#264F77] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                  <span>Es dürfen unendlich viele Karten aufgenommen werden, wenn ein Spieler nicht kann oder nicht will</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Close Button Bottom */}
          <div className="mt-8 text-center">
            <button
              onClick={onClose}
              className="px-8 py-3 bg-[#264F77] text-white font-medium rounded-lg hover:bg-[#1e3d5c] transition-colors"
            >
              Verstanden
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export const TschauSepp = () => {
  const navigate = useNavigate();
  
  // Modal state
  const [showInfo, setShowInfo] = useState(false);
  
  // Game state
  const [gameStarted, setGameStarted] = useState(false);
  const [playerCount, setPlayerCount] = useState(2);
  const [playerNames, setPlayerNames] = useState(['', '']);
  const [totalRounds, setTotalRounds] = useState(10);
  const [doubleRoundsPerPlayer, setDoubleRoundsPerPlayer] = useState(2);
  const [startingPlayer, setStartingPlayer] = useState(0);
  
  // Online game state
  const [isCreatingOnlineGame, setIsCreatingOnlineGame] = useState(false);
  const [onlineRoundId, setOnlineRoundId] = useState(null);
  const [onlineHostKey, setOnlineHostKey] = useState(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [copied, setCopied] = useState(false);
  
  // Game progress
  const [currentRound, setCurrentRound] = useState(1);
  const [scores, setScores] = useState([]);
  const [roundScores, setRoundScores] = useState([]);
  const [multiplier, setMultiplier] = useState(1);
  const [doubleRoundsUsed, setDoubleRoundsUsed] = useState([]);
  const [currentPlayer, setCurrentPlayer] = useState(0);
  const [gameHistory, setGameHistory] = useState([]);
  const [gameEnded, setGameEnded] = useState(false);

  // Edit history state
  const [editingCell, setEditingCell] = useState(null); // {roundIdx, playerIdx}
  const [editValue, setEditValue] = useState('');
  
  // Player edit modal state
  const [showPlayerEditModal, setShowPlayerEditModal] = useState(false);
  const [editPlayerNames, setEditPlayerNames] = useState([]);

  // Initialize scores when game starts
  useEffect(() => {
    if (gameStarted) {
      setScores(Array(playerCount).fill(0));
      setRoundScores(Array(playerCount).fill(0));
      setDoubleRoundsUsed(Array(playerCount).fill(0));
      setCurrentPlayer(startingPlayer);
    }
  }, [gameStarted, playerCount, startingPlayer]);

  // Update player names array when count changes
  useEffect(() => {
    setPlayerNames(prev => {
      const newNames = [...prev];
      while (newNames.length < playerCount) {
        newNames.push('');
      }
      return newNames.slice(0, playerCount);
    });
  }, [playerCount]);

  const handlePlayerNameChange = (index, name) => {
    const newNames = [...playerNames];
    newNames[index] = name;
    setPlayerNames(newNames);
  };

  // Create online game
  const createOnlineGame = async (filledNames) => {
    setIsCreatingOnlineGame(true);
    try {
      const response = await fetch(`${API_URL}/api/tschausepp/rounds`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          player_names: filledNames,
          total_rounds: totalRounds,
          double_rounds_per_player: doubleRoundsPerPlayer,
          starting_player_index: startingPlayer
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setOnlineRoundId(data.id);
        setOnlineHostKey(data.host_key);
        // Store in localStorage for persistence
        localStorage.setItem('tschausepp_round_id', data.id);
        localStorage.setItem('tschausepp_host_key', data.host_key);
        return data;
      }
    } catch (err) {
      console.error('Failed to create online game:', err);
    } finally {
      setIsCreatingOnlineGame(false);
    }
    return null;
  };

  const startGame = async () => {
    const filledNames = playerNames.map((name, i) => name.trim() || `Spieler ${i + 1}`);
    setPlayerNames(filledNames);
    
    // Create online game for sharing
    const onlineGame = await createOnlineGame(filledNames);
    if (onlineGame) {
      setShowShareModal(true);
    }
    
    setGameStarted(true);
  };

  // Copy share link
  const copyShareLink = async () => {
    const url = `${window.location.origin}/runde/${onlineRoundId}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  // Share via WhatsApp
  const shareViaWhatsApp = () => {
    const url = `${window.location.origin}/runde/${onlineRoundId}`;
    const text = `🃏 Schau dir unser Tschau-Sepp Spiel an!\n\nSpieler: ${playerNames.join(', ')}\n\nLive-Spielstand: ${url}`;
    const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(whatsappUrl, '_blank');
  };

  // Go to host view
  const goToHostView = () => {
    if (onlineRoundId && onlineHostKey) {
      navigate(`/runde/${onlineRoundId}?key=${onlineHostKey}`);
    }
  };

  const handleRoundScoreChange = (index, value) => {
    const newScores = [...roundScores];
    newScores[index] = parseInt(value) || 0;
    setRoundScores(newScores);
  };

  const callDoubleRound = async (playerIndex) => {
    if (doubleRoundsUsed[playerIndex] < doubleRoundsPerPlayer) {
      setMultiplier(prev => prev * 2);
      const newUsed = [...doubleRoundsUsed];
      newUsed[playerIndex]++;
      setDoubleRoundsUsed(newUsed);
      
      // Sync with backend
      if (onlineRoundId && onlineHostKey) {
        try {
          await fetch(`${API_URL}/api/tschausepp/rounds/${onlineRoundId}/call-double?key=${onlineHostKey}&player_index=${playerIndex}`, {
            method: 'POST'
          });
        } catch (err) {
          console.error('Failed to sync double round:', err);
        }
      }
    }
  };

  const underFinish = async () => {
    const newRoundScores = roundScores.map((score) => {
      if (score === 0) return 0;
      return score * 2;
    });
    setRoundScores(newRoundScores);
    
    // Sync with backend
    if (onlineRoundId && onlineHostKey) {
      try {
        await fetch(`${API_URL}/api/tschausepp/rounds/${onlineRoundId}/under-finish?key=${onlineHostKey}`, {
          method: 'POST'
        });
      } catch (err) {
        console.error('Failed to sync under-finish:', err);
      }
    }
  };

  const saveRound = async () => {
    const finalRoundScores = roundScores.map(score => score * multiplier);
    const newTotalScores = scores.map((score, i) => score + finalRoundScores[i]);
    setScores(newTotalScores);
    
    setGameHistory(prev => [...prev, {
      round: currentRound,
      scores: finalRoundScores,
      multiplier,
      starter: currentPlayer
    }]);
    
    // Sync with backend if online game
    if (onlineRoundId && onlineHostKey) {
      try {
        await fetch(`${API_URL}/api/tschausepp/rounds/${onlineRoundId}/save-round?key=${onlineHostKey}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            round_scores: roundScores,
            multiplier: multiplier
          })
        });
      } catch (err) {
        console.error('Failed to sync round with server:', err);
      }
    }
    
    if (currentRound >= totalRounds) {
      setGameEnded(true);
    } else {
      setCurrentRound(prev => prev + 1);
      setRoundScores(Array(playerCount).fill(0));
      setMultiplier(1);
      setCurrentPlayer(prev => (prev + 1) % playerCount);
    }
  };

  const resetGame = () => {
    setGameStarted(false);
    setGameEnded(false);
    setCurrentRound(1);
    setScores([]);
    setRoundScores([]);
    setMultiplier(1);
    setDoubleRoundsUsed([]);
    setGameHistory([]);
    setOnlineRoundId(null);
    setOnlineHostKey(null);
    localStorage.removeItem('tschausepp_round_id');
    localStorage.removeItem('tschausepp_host_key');
  };

  // ---- EDIT HISTORY SCORES ----
  const startEditCell = (roundIdx, playerIdx) => {
    const currentScore = gameHistory[roundIdx].scores[playerIdx];
    setEditingCell({ roundIdx, playerIdx });
    setEditValue(String(currentScore));
  };

  const cancelEdit = () => {
    setEditingCell(null);
    setEditValue('');
  };

  const saveEditCell = async () => {
    if (!editingCell) return;
    const { roundIdx, playerIdx } = editingCell;
    const newScore = parseInt(editValue) || 0;
    const oldScore = gameHistory[roundIdx].scores[playerIdx];

    if (newScore === oldScore) {
      cancelEdit();
      return;
    }

    // Update locally
    const newHistory = gameHistory.map((round, i) => {
      if (i === roundIdx) {
        const newScores = [...round.scores];
        newScores[playerIdx] = newScore;
        return { ...round, scores: newScores, edited: true };
      }
      return round;
    });
    setGameHistory(newHistory);

    // Recalculate totals from history
    const newTotals = Array(playerCount).fill(0);
    newHistory.forEach(round => {
      round.scores.forEach((s, idx) => {
        newTotals[idx] += s;
      });
    });
    setScores(newTotals);

    // Sync with backend
    if (onlineRoundId && onlineHostKey) {
      try {
        await fetch(`${API_URL}/api/tschausepp/rounds/${onlineRoundId}/edit-history?key=${onlineHostKey}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            round_index: roundIdx,
            player_index: playerIdx,
            new_score: newScore
          })
        });
      } catch (err) {
        console.error('Failed to sync edit:', err);
      }
    }

    cancelEdit();
  };

  const handleEditKeyDown = (e) => {
    if (e.key === 'Enter') saveEditCell();
    if (e.key === 'Escape') cancelEdit();
  };

  // ---- NEW GAME (keep players) ----
  const startNewGameWithPlayers = async () => {
    if (onlineRoundId && onlineHostKey) {
      try {
        const response = await fetch(`${API_URL}/api/tschausepp/rounds/${onlineRoundId}/new-game?key=${onlineHostKey}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            total_rounds: totalRounds,
            double_rounds_per_player: doubleRoundsPerPlayer
          })
        });
        if (response.ok) {
          const data = await response.json();
          setOnlineRoundId(data.id);
          setOnlineHostKey(data.host_key);
          localStorage.setItem('tschausepp_round_id', data.id);
          localStorage.setItem('tschausepp_host_key', data.host_key);
        }
      } catch (err) {
        console.error('Failed to create new game:', err);
      }
    }

    // Reset game state but keep players
    setGameEnded(false);
    setCurrentRound(1);
    setScores(Array(playerCount).fill(0));
    setRoundScores(Array(playerCount).fill(0));
    setMultiplier(1);
    setDoubleRoundsUsed(Array(playerCount).fill(0));
    setGameHistory([]);
    setCurrentPlayer(0);
  };

  // ---- PLAYER EDIT ----
  const openPlayerEdit = () => {
    setEditPlayerNames(playerNames.map(n => n));
    setShowPlayerEditModal(true);
  };

  const handleEditPlayerName = (index, name) => {
    const newNames = [...editPlayerNames];
    newNames[index] = name;
    setEditPlayerNames(newNames);
  };

  const addEditPlayer = () => {
    setEditPlayerNames(prev => [...prev, '']);
  };

  const removeEditPlayer = (index) => {
    if (editPlayerNames.length <= 2) return;
    setEditPlayerNames(prev => prev.filter((_, i) => i !== index));
  };

  const savePlayerEdits = async () => {
    const actions = [];
    const currentCount = playerNames.length;
    const newCount = editPlayerNames.length;

    // Detect renames
    for (let i = 0; i < Math.min(currentCount, newCount); i++) {
      const newName = editPlayerNames[i].trim() || `Spieler ${i + 1}`;
      if (newName !== playerNames[i]) {
        actions.push({ action: 'rename', index: i, name: newName });
      }
    }

    // Detect removals (from end)
    if (newCount < currentCount) {
      for (let i = currentCount - 1; i >= newCount; i--) {
        actions.push({ action: 'remove', index: i });
      }
    }

    // Detect additions
    if (newCount > currentCount) {
      for (let i = currentCount; i < newCount; i++) {
        actions.push({ action: 'add', name: editPlayerNames[i].trim() || `Spieler ${i + 1}` });
      }
    }

    // Apply locally
    const finalNames = editPlayerNames.map((n, i) => n.trim() || `Spieler ${i + 1}`);
    setPlayerNames(finalNames);
    setPlayerCount(finalNames.length);

    // Adjust scores, roundScores, doubleRoundsUsed arrays
    const adjustArray = (arr, newLen) => {
      const result = [...arr];
      while (result.length < newLen) result.push(0);
      return result.slice(0, newLen);
    };

    setScores(prev => {
      const adjusted = adjustArray(prev, finalNames.length);
      // Recalculate from history
      const totals = Array(finalNames.length).fill(0);
      gameHistory.forEach(round => {
        const paddedScores = adjustArray(round.scores, finalNames.length);
        paddedScores.forEach((s, idx) => { totals[idx] += s; });
      });
      return totals;
    });
    setRoundScores(prev => adjustArray(prev, finalNames.length));
    setDoubleRoundsUsed(prev => adjustArray(prev, finalNames.length));

    // Adjust history entries
    setGameHistory(prev => prev.map(round => ({
      ...round,
      scores: adjustArray(round.scores, finalNames.length)
    })));

    // Sync with backend
    if (onlineRoundId && onlineHostKey && actions.length > 0) {
      try {
        await fetch(`${API_URL}/api/tschausepp/rounds/${onlineRoundId}/edit-players?key=${onlineHostKey}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ players: actions })
        });
      } catch (err) {
        console.error('Failed to sync player edits:', err);
      }
    }

    setShowPlayerEditModal(false);
  };

  const randomizeStartingPlayer = () => {
    setStartingPlayer(Math.floor(Math.random() * playerCount));
  };

  const getWinner = () => {
    const minScore = Math.min(...scores);
    const winnerIndex = scores.indexOf(minScore);
    return playerNames[winnerIndex];
  };

  return (
    <div className="min-h-screen relative">
      {/* Animated Jass Cards Background */}
      <JassCardsBackground />

      {/* Info Modal */}
      <InfoModal isOpen={showInfo} onClose={() => setShowInfo(false)} />

      {/* Share Modal */}
      {showShareModal && onlineRoundId && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          onClick={() => setShowShareModal(false)}
        >
          <div className="absolute inset-0 bg-black/60" />
          <div 
            className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
            onClick={e => e.stopPropagation()}
          >
            <h3 className="text-xl font-semibold text-[#264F77] mb-2 flex items-center gap-2">
              <Share2 size={24} />
              Spiel teilen
            </h3>
            <p className="text-gray-600 mb-4 text-sm">
              Teile diesen Link mit deinen Mitspielern, damit sie den Spielstand live verfolgen können:
            </p>
            <div className="flex gap-2 mb-4">
              <input
                type="text"
                value={`${window.location.origin}/runde/${onlineRoundId}`}
                readOnly
                className="flex-1 px-4 py-3 bg-gray-100 border border-gray-200 rounded-lg text-sm text-gray-700"
              />
              <button
                onClick={copyShareLink}
                className={`px-4 py-3 rounded-lg font-medium transition-all ${
                  copied 
                    ? 'bg-green-500 text-white' 
                    : 'bg-[#264F77] text-white hover:bg-[#1e3d5c]'
                }`}
              >
                {copied ? <Check size={20} /> : <Copy size={20} />}
              </button>
            </div>
            
            {/* WhatsApp Share Button */}
            <button
              onClick={shareViaWhatsApp}
              className="w-full py-3 bg-[#25D366] text-white font-medium rounded-lg hover:bg-[#20bd5a] transition-colors flex items-center justify-center gap-2 mb-4"
            >
              <MessageCircle size={20} />
              Via WhatsApp teilen
            </button>
            
            <div className="flex gap-2">
              <button
                onClick={() => setShowShareModal(false)}
                className="flex-1 py-3 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors"
              >
                Hier weiterspielen
              </button>
              <button
                onClick={goToHostView}
                className="flex-1 py-3 bg-[#264F77] text-white font-medium rounded-lg hover:bg-[#1e3d5c] transition-colors flex items-center justify-center gap-2"
              >
                <ExternalLink size={18} />
                Host-Ansicht
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Page Content */}
      <div className="relative z-10 min-h-screen flex flex-col">

        {/* Player Edit Modal */}
        {showPlayerEditModal && (
          <div 
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={() => setShowPlayerEditModal(false)}
          >
            <div className="absolute inset-0 bg-black/60" />
            <div 
              className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto"
              onClick={e => e.stopPropagation()}
              data-testid="player-edit-modal"
            >
              <button
                onClick={() => setShowPlayerEditModal(false)}
                className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                <X size={16} className="text-gray-600" />
              </button>

              <h3 className="text-xl font-semibold text-[#264F77] mb-2 flex items-center gap-2">
                <Users size={24} />
                Spieler bearbeiten
              </h3>
              <p className="text-gray-600 mb-5 text-sm">
                Spieler umbenennen, hinzufügen oder entfernen.
              </p>

              <div className="space-y-3 mb-6">
                {editPlayerNames.map((name, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <span className="w-6 text-sm text-gray-400 text-center">{index + 1}.</span>
                    <input
                      type="text"
                      value={name}
                      onChange={(e) => handleEditPlayerName(index, e.target.value)}
                      placeholder={`Spieler ${index + 1}`}
                      className="flex-1 px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#264F77] focus:border-transparent outline-none transition-all"
                      data-testid={`edit-player-name-${index}`}
                    />
                    {editPlayerNames.length > 2 && (
                      <button
                        onClick={() => removeEditPlayer(index)}
                        className="w-10 h-10 flex items-center justify-center rounded-lg bg-red-50 text-red-500 hover:bg-red-100 transition-colors"
                        data-testid={`remove-player-${index}`}
                      >
                        <UserMinus size={18} />
                      </button>
                    )}
                  </div>
                ))}
              </div>

              <button
                onClick={addEditPlayer}
                className="w-full py-3 mb-4 border-2 border-dashed border-gray-300 text-gray-500 font-medium rounded-lg hover:border-[#264F77] hover:text-[#264F77] transition-colors flex items-center justify-center gap-2"
                data-testid="add-player-btn"
              >
                <UserPlus size={18} />
                Spieler hinzufügen
              </button>

              <div className="flex gap-2">
                <button
                  onClick={() => setShowPlayerEditModal(false)}
                  className="flex-1 py-3 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Abbrechen
                </button>
                <button
                  onClick={savePlayerEdits}
                  className="flex-1 py-3 bg-[#264F77] text-white font-medium rounded-lg hover:bg-[#1e3d5c] transition-colors"
                  data-testid="save-players-btn"
                >
                  Speichern
                </button>
              </div>
            </div>
          </div>
        )}
        {/* Top Bar with Home Link */}
        <div className="px-4 sm:px-6 lg:px-8 pt-6">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <Link 
              to="/" 
              className="flex items-center space-x-2 px-4 py-2 bg-white/10 backdrop-blur-sm text-white rounded-lg hover:bg-white/20 transition-colors"
            >
              <Home size={18} />
              <span className="text-sm font-medium">Zurück zu LIVAG</span>
            </Link>
            {gameStarted && !gameEnded && (
              <div className="flex items-center gap-2">
                {onlineRoundId && (
                  <button
                    onClick={() => setShowShareModal(true)}
                    className="flex items-center space-x-2 px-4 py-2 bg-white/10 backdrop-blur-sm text-white rounded-lg hover:bg-white/20 transition-colors"
                  >
                    <Share2 size={18} />
                    <span className="text-sm font-medium hidden sm:inline">Teilen</span>
                  </button>
                )}
                <button
                  onClick={openPlayerEdit}
                  className="flex items-center space-x-2 px-4 py-2 bg-white/10 backdrop-blur-sm text-white rounded-lg hover:bg-white/20 transition-colors"
                  data-testid="edit-players-ingame-btn"
                >
                  <Pencil size={18} />
                  <span className="text-sm font-medium hidden sm:inline">Spieler</span>
                </button>
                <button
                  onClick={resetGame}
                  className="flex items-center space-x-2 px-4 py-2 bg-white/10 backdrop-blur-sm text-white rounded-lg hover:bg-white/20 transition-colors"
                >
                  <RotateCcw size={18} />
                  <span className="text-sm font-medium hidden sm:inline">Neues Spiel</span>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Main Content */}
        <main className="flex-1 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Page Title with Info Button */}
          <div className="flex items-center justify-center gap-3 mb-8">
            <h1 className="text-3xl md:text-4xl font-bold text-white drop-shadow-lg" style={{ fontFamily: "'Playfair Display', serif" }}>
              Tschau-Sepp Rechner
            </h1>
            <button
              onClick={() => setShowInfo(true)}
              className="w-10 h-10 flex items-center justify-center rounded-full bg-white/20 backdrop-blur-sm hover:bg-white/30 transition-colors group"
              aria-label="Spielregeln anzeigen"
            >
              <Info size={20} className="text-white group-hover:scale-110 transition-transform" />
            </button>
          </div>

          {!gameStarted ? (
            /* Setup Screen */
            <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-6 md:p-8">
              <div className="flex items-center space-x-3 mb-6">
                <Trophy className="text-[#264F77]" size={28} />
                <h2 className="text-2xl font-semibold text-[#264F77]">Spiel einrichten</h2>
              </div>
              <p className="text-gray-600 mb-8">
                Konfigurieren Sie die Spieleinstellungen für Ihr Tschau-Sepp Spiel
              </p>

              {/* Player Count */}
              <div className="mb-6">
                <label className="flex items-center space-x-2 text-gray-700 font-medium mb-3">
                  <Users size={18} className="text-[#264F77]" />
                  <span>Anzahl Spieler</span>
                </label>
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => setPlayerCount(Math.max(2, playerCount - 1))}
                    className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    <Minus size={18} />
                  </button>
                  <span className="text-2xl font-bold text-[#264F77] w-12 text-center">{playerCount}</span>
                  <button
                    onClick={() => setPlayerCount(Math.min(8, playerCount + 1))}
                    className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    <Plus size={18} />
                  </button>
                </div>
              </div>

              {/* Player Names */}
              <div className="mb-6">
                <label className="text-gray-700 font-medium mb-3 block">Spielernamen</label>
                <div className="space-y-3">
                  {playerNames.map((name, index) => (
                    <input
                      key={index}
                      type="text"
                      value={name}
                      onChange={(e) => handlePlayerNameChange(index, e.target.value)}
                      placeholder={`Spieler ${index + 1}`}
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#264F77] focus:border-transparent outline-none transition-all"
                    />
                  ))}
                </div>
              </div>

              {/* Total Rounds */}
              <div className="mb-6">
                <label className="text-gray-700 font-medium mb-3 block">Gesamtzahl Runden</label>
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => setTotalRounds(Math.max(1, totalRounds - 1))}
                    className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    <Minus size={18} />
                  </button>
                  <span className="text-2xl font-bold text-[#264F77] w-12 text-center">{totalRounds}</span>
                  <button
                    onClick={() => setTotalRounds(totalRounds + 1)}
                    className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    <Plus size={18} />
                  </button>
                </div>
              </div>

              {/* Double Rounds per Player */}
              <div className="mb-6">
                <label className="text-gray-700 font-medium mb-3 block">Anzahl Doppelrunden pro Spieler</label>
                <div className="flex items-center space-x-4">
                  <button
                    onClick={() => setDoubleRoundsPerPlayer(Math.max(0, doubleRoundsPerPlayer - 1))}
                    className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    <Minus size={18} />
                  </button>
                  <span className="text-2xl font-bold text-[#264F77] w-12 text-center">{doubleRoundsPerPlayer}</span>
                  <button
                    onClick={() => setDoubleRoundsPerPlayer(doubleRoundsPerPlayer + 1)}
                    className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                  >
                    <Plus size={18} />
                  </button>
                </div>
              </div>

              {/* Starting Player */}
              <div className="mb-8">
                <label className="flex items-center space-x-2 text-gray-700 font-medium mb-3">
                  <Shuffle size={18} className="text-[#264F77]" />
                  <span>Wer beginnt das 1. Spiel?</span>
                </label>
                <div className="flex flex-wrap gap-2">
                  {playerNames.map((name, index) => (
                    <button
                      key={index}
                      onClick={() => setStartingPlayer(index)}
                      className={`px-4 py-2 rounded-lg font-medium transition-all ${
                        startingPlayer === index
                          ? 'bg-[#264F77] text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {name || `Spieler ${index + 1}`}
                    </button>
                  ))}
                  <button
                    onClick={randomizeStartingPlayer}
                    className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-all flex items-center space-x-2"
                  >
                    <Shuffle size={16} />
                    <span>Zufällig</span>
                  </button>
                </div>
              </div>

              {/* Start Game Button */}
              <button
                onClick={startGame}
                disabled={isCreatingOnlineGame}
                className="w-full py-4 bg-[#264F77] text-white font-semibold rounded-lg hover:bg-[#1e3d5c] transition-colors shadow-lg disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isCreatingOnlineGame ? (
                  <>
                    <Loader2 className="animate-spin" size={20} />
                    Spiel wird erstellt...
                  </>
                ) : (
                  'SPIEL STARTEN'
                )}
              </button>
            </div>
          ) : gameEnded ? (
            /* Game Over Screen */
            <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-6 md:p-8 text-center">
              <Trophy className="mx-auto text-yellow-500 mb-4" size={64} />
              <h2 className="text-3xl font-bold text-[#264F77] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                Spiel beendet!
              </h2>
              <p className="text-xl text-gray-600 mb-6">
                Gewinner: <span className="font-bold text-[#264F77]">{getWinner()}</span>
              </p>
              
              {/* Final Scores */}
              <div className="bg-gray-50 rounded-xl p-6 mb-6">
                <h3 className="text-lg font-semibold text-[#264F77] mb-4">Endstand</h3>
                <div className="space-y-2">
                  {playerNames.map((name, index) => (
                    <div key={index} className="flex justify-between items-center py-2 border-b border-gray-200 last:border-0">
                      <span className="font-medium">{name}</span>
                      <span className={`font-bold ${scores[index] === Math.min(...scores) ? 'text-green-600' : 'text-gray-700'}`}>
                        {scores[index]} Punkte
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Game History - editable */}
              {gameHistory.length > 0 && (
                <div className="bg-gray-50 rounded-xl p-6 mb-6 text-left">
                  <h3 className="text-lg font-semibold text-[#264F77] mb-4">Rundenverlauf</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid="game-history-table-ended">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-2 px-3 text-gray-600 text-sm">Runde</th>
                          {playerNames.map((name, i) => (
                            <th key={i} className="text-center py-2 px-3 text-gray-600 text-sm">{name}</th>
                          ))}
                          <th className="text-center py-2 px-3 text-gray-600 text-sm">Multi</th>
                        </tr>
                      </thead>
                      <tbody>
                        {gameHistory.map((round, i) => (
                          <tr key={i} className={`border-b border-gray-100 ${round.edited ? 'bg-amber-50' : ''}`}>
                            <td className="py-2 px-3 text-gray-700 flex items-center gap-1">
                              {round.round}
                              {round.edited && <Pencil size={12} className="text-amber-500" />}
                            </td>
                            {round.scores.map((score, j) => (
                              <td key={j} className="text-center py-2 px-3">
                                {editingCell?.roundIdx === i && editingCell?.playerIdx === j ? (
                                  <input
                                    type="number"
                                    value={editValue}
                                    onChange={(e) => setEditValue(e.target.value)}
                                    onBlur={saveEditCell}
                                    onKeyDown={handleEditKeyDown}
                                    className="w-16 px-2 py-1 border-2 border-[#264F77] rounded text-center font-medium focus:outline-none"
                                    autoFocus
                                    data-testid={`edit-cell-${i}-${j}`}
                                  />
                                ) : (
                                  <button
                                    onClick={() => startEditCell(i, j)}
                                    className="w-full font-medium hover:bg-[#264F77]/10 rounded px-2 py-1 transition-colors cursor-pointer"
                                    title="Klicken zum Bearbeiten"
                                    data-testid={`score-cell-${i}-${j}`}
                                  >
                                    {score}
                                  </button>
                                )}
                              </td>
                            ))}
                            <td className="text-center py-2 px-3 text-[#264F77]">{round.multiplier}x</td>
                          </tr>
                        ))}
                        <tr className="bg-gray-100 font-bold">
                          <td className="py-3 px-3 text-[#264F77]">Total</td>
                          {scores.map((score, i) => (
                            <td key={i} className="text-center py-3 px-3 text-[#264F77]">{score}</td>
                          ))}
                          <td className="text-center py-3 px-3">-</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={startNewGameWithPlayers}
                  className="flex-1 py-4 bg-[#264F77] text-white font-semibold rounded-lg hover:bg-[#1e3d5c] transition-colors shadow-lg flex items-center justify-center gap-2"
                  data-testid="new-game-keep-players-btn"
                >
                  <RefreshCw size={20} />
                  Neues Spiel starten
                </button>
                <button
                  onClick={openPlayerEdit}
                  className="flex-1 py-4 bg-white border-2 border-[#264F77] text-[#264F77] font-semibold rounded-lg hover:bg-[#264F77]/5 transition-colors flex items-center justify-center gap-2"
                  data-testid="edit-players-btn"
                >
                  <Users size={20} />
                  Spieler bearbeiten
                </button>
              </div>
              <button
                onClick={resetGame}
                className="mt-3 w-full py-3 bg-gray-100 text-gray-600 font-medium rounded-lg hover:bg-gray-200 transition-colors"
                data-testid="complete-reset-btn"
              >
                Komplett neues Spiel
              </button>
            </div>
          ) : (
            /* Game Screen */
            <div className="space-y-6">
              {/* Round Info Header */}
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-white drop-shadow">
                  Runde {currentRound} von {totalRounds}
                </h2>
              </div>

              {/* Current Round Card */}
              <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold text-gray-700">Aktuelle Runde</h3>
                  <div className="flex items-center space-x-3">
                    <span className="px-3 py-1 bg-[#264F77] text-white text-sm rounded-full flex items-center space-x-1">
                      <Shuffle size={14} />
                      <span>{playerNames[currentPlayer]}</span>
                    </span>
                    <span className="px-3 py-1 bg-[#1e3d5c] text-white text-sm rounded-full">
                      Multiplikator: {multiplier}x
                    </span>
                  </div>
                </div>

                {/* Double Round Buttons */}
                <div className="mb-4">
                  <p className="text-sm text-gray-600 mb-2">Doppelrunden-Ansagen:</p>
                  <div className="flex flex-wrap gap-2">
                    {playerNames.map((name, index) => (
                      <button
                        key={index}
                        onClick={() => callDoubleRound(index)}
                        disabled={doubleRoundsUsed[index] >= doubleRoundsPerPlayer}
                        className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-all ${
                          doubleRoundsUsed[index] >= doubleRoundsPerPlayer
                            ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                            : 'bg-[#264F77] text-white hover:bg-[#1e3d5c]'
                        }`}
                      >
                        {name} ({doubleRoundsPerPlayer - doubleRoundsUsed[index]})
                      </button>
                    ))}
                  </div>
                </div>

                {/* Under-Finish Button */}
                <button
                  onClick={underFinish}
                  className="w-full py-3 bg-gradient-to-r from-[#264F77] to-[#1e3d5c] text-white font-medium rounded-lg hover:opacity-90 transition-opacity flex items-center justify-center space-x-2 mb-6"
                >
                  <Zap size={18} />
                  <span>Under-Finish</span>
                </button>

                {/* Player Scores Input */}
                <div className="space-y-4">
                  {playerNames.map((name, index) => (
                    <div key={index} className="flex items-center space-x-4">
                      <span className="w-24 font-medium text-gray-700">{name}</span>
                      <input
                        type="number"
                        value={roundScores[index] || 0}
                        onChange={(e) => handleRoundScoreChange(index, e.target.value)}
                        className="flex-1 px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#264F77] focus:border-transparent outline-none text-center font-semibold"
                        min="0"
                      />
                    </div>
                  ))}
                </div>

                {/* Save Round Button */}
                <button
                  onClick={saveRound}
                  className="w-full mt-6 py-4 bg-[#264F77] text-white font-semibold rounded-lg hover:bg-[#1e3d5c] transition-colors flex items-center justify-center space-x-2"
                >
                  <Save size={18} />
                  <span>RUNDE SPEICHERN</span>
                </button>
              </div>

              {/* Score Board */}
              {gameHistory.length > 0 && (
                <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-6">
                  <h3 className="text-lg font-semibold text-[#264F77] mb-4">Spielstand</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid="game-history-table">
                      <thead>
                        <tr className="border-b border-gray-200">
                          <th className="text-left py-2 px-3 text-gray-600">Runde</th>
                          {playerNames.map((name, i) => (
                            <th key={i} className="text-center py-2 px-3 text-gray-600">{name}</th>
                          ))}
                          <th className="text-center py-2 px-3 text-gray-600">Multi</th>
                        </tr>
                      </thead>
                      <tbody>
                        {gameHistory.map((round, i) => (
                          <tr key={i} className={`border-b border-gray-100 ${round.edited ? 'bg-amber-50' : ''}`}>
                            <td className="py-2 px-3 text-gray-700 flex items-center gap-1">
                              {round.round}
                              {round.edited && <Pencil size={12} className="text-amber-500" />}
                            </td>
                            {round.scores.map((score, j) => (
                              <td key={j} className="text-center py-2 px-3">
                                {editingCell?.roundIdx === i && editingCell?.playerIdx === j ? (
                                  <input
                                    type="number"
                                    value={editValue}
                                    onChange={(e) => setEditValue(e.target.value)}
                                    onBlur={saveEditCell}
                                    onKeyDown={handleEditKeyDown}
                                    className="w-16 px-2 py-1 border-2 border-[#264F77] rounded text-center font-medium focus:outline-none"
                                    autoFocus
                                    data-testid={`edit-cell-${i}-${j}`}
                                  />
                                ) : (
                                  <button
                                    onClick={() => startEditCell(i, j)}
                                    className="w-full font-medium hover:bg-[#264F77]/10 rounded px-2 py-1 transition-colors cursor-pointer"
                                    title="Klicken zum Bearbeiten"
                                    data-testid={`score-cell-${i}-${j}`}
                                  >
                                    {score}
                                  </button>
                                )}
                              </td>
                            ))}
                            <td className="text-center py-2 px-3 text-[#264F77]">{round.multiplier}x</td>
                          </tr>
                        ))}
                        <tr className="bg-gray-50 font-bold">
                          <td className="py-3 px-3 text-[#264F77]">Total</td>
                          {scores.map((score, i) => (
                            <td key={i} className="text-center py-3 px-3 text-[#264F77]">{score}</td>
                          ))}
                          <td className="text-center py-3 px-3">-</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
        </main>

        {/* Footer */}
        <footer className="py-6 text-center text-white/60 text-sm relative z-10">
          <p>LIVAG Tschau-Sepp Rechner</p>
        </footer>
      </div>
    </div>
  );
};
